import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Modal from 'react-modal';

Modal.setAppElement('#root');

const Exam = () => {
  const { subjectId } = useParams();
  const { state } = useLocation();
  const { student, currentSessionId, currentTermId, subjectDetails: preloadedSubject, classId: currentClassId } = state || {};
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  const [subjectDetails, setSubjectDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [timeUsed, setTimeUsed] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIntroModalOpen, setIsIntroModalOpen] = useState(true);
  const [isBackConfirmOpen, setIsBackConfirmOpen] = useState(false);
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const [score, setScore] = useState(null);
  const [isScoreModalOpen, setIsScoreModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [transition, setTransition] = useState(null);
  const questionsPerPage = 10;

  useEffect(() => {
    if (!student || !student.id || !subjectId) {
      setError("Invalid student or subject data");
      setLoading(false);
      return;
    }

    if (!currentSessionId || !currentTermId) {
      setError("Session and Term information is missing. Please return to dashboard.");
      setLoading(false);
      return;
    }

    const fetchExamData = async () => {
      setLoading(true);
      try {
        const studentRef = doc(db, 'students', student.id);
        const studentSnap = await getDoc(studentRef);
        if (!studentSnap.exists()) {
          setError("Student not found");
          setLoading(false);
          return;
        }
        const freshStudentData = { id: studentSnap.id, ...studentSnap.data() };

        // Find the correct enrollment
        const enrollment = freshStudentData.enrollments.find(enroll =>
          enroll.subjects?.some(sub => sub.subjectId === subjectId)
        );
        if (!enrollment) {          
          setError("No enrollment found for this subject");
          setLoading(false);
          return;
        }

        const subject = enrollment.subjects.find(sub => sub.subjectId === subjectId);
        if (!subject) {
          setError("Subject not found in enrollment");
          setLoading(false);
          return;
        }

                // === STRONGEST VERSION - Re-find enrollment using currentClassId ===
        const currentEnrollment = freshStudentData.enrollments.find(enroll => 
          enroll.classId === currentClassId
        );

        if (!currentEnrollment) {
          setError("Enrollment for current class not found.");
          setLoading(false);
          return;
        }

        const currentSubject = currentEnrollment.subjects?.find(sub => sub.subjectId === subjectId);
        if (!currentSubject) {
          setError("Subject not found in current enrollment.");
          setLoading(false);
          return;
        }

        const scores = currentSubject.scores || {};
        const examScore = scores.exam;
        const examSessionId = scores.examSessionId;
        const examTermId = scores.examTermId;

        const hasTakenExamThisTerm = 
          examSessionId === currentSessionId && 
          examTermId === currentTermId;

        if (hasTakenExamThisTerm) {
          // Only block if score is NOT 0 (allow retake if score is 0)
          if (examScore !== 0) {
            const scoreMessage = `Your previous score: ${examScore}/60.`;
            setError(`You have already taken this exam in the current term. 
                      ${scoreMessage}
                      You cannot retake because you already have a score.
                      Contact your teacher for a retake.`);
            setLoading(false);
            return;
          }
        }

        // === NEW: Use preloaded subject details from Dashboard (Best & Fastest) ===
        if (preloadedSubject && preloadedSubject.questionSetId) {
          setSubjectDetails(preloadedSubject);

          // Fetch questions using the preloaded questionSetId
          const questionRef = doc(db, 'questions', preloadedSubject.questionSetId);
          const questionSnap = await getDoc(questionRef);
          if (!questionSnap.exists()) {
            setError("Question set not found");
            setLoading(false);
            return;
          }

          const allQuestions = questionSnap.data().questions || [];
          setQuestions(allQuestions);

          const shuffled = shuffleArray([...allQuestions])
            .slice(0, preloadedSubject.questionCount || allQuestions.length);

          const questionsWithShuffledOptions = shuffled.map(q => {
            const options = [...q.options];
            const shuffledIndices = Array.from({ length: options.length }, (_, i) => i);
            shuffleArray(shuffledIndices);
            const shuffledOptions = shuffledIndices.map(i => options[i]);
            const newCorrectIndex = shuffledIndices.indexOf(q.correctIndex);
            return {
              ...q,
              options: shuffledOptions,
              correctIndex: newCorrectIndex
            };
          });

          setShuffledQuestions(questionsWithShuffledOptions);
          setLoading(false);
          return;   // ← Skip the old class fetching logic below
        }

        // === FALLBACK: Old logic (only runs if preloadedSubject is missing) ===
        const classId = enrollment.classId;
        const classRef = doc(db, 'classes', classId);
        const classSnap = await getDoc(classRef);

        if (!classSnap.exists()) {
          setError("Class not found");
          setLoading(false);
          return;
        }

        const classData = classSnap.data();
        const classSubject = classData.subjects?.find(sub => sub.id === subjectId);

        if (!classSubject) {
          setError("Subject not found in class");
          setLoading(false);
          return;
        }
        if (!classSubject.questionSetId) {
          setError("No CBT assigned for this subject. Please contact your teacher.");
          setLoading(false);
          return;
        }

        const now = new Date();
        const activation = classSubject.activationDate ? new Date(classSubject.activationDate) : null;
        const deactivation = classSubject.deactivationDate ? new Date(classSubject.deactivationDate) : null;
        const isActive = (!activation || !deactivation) || (now >= activation && now <= deactivation);

        if (!isActive) {
          setError("CBT is not currently active");
          setLoading(false);
          return;
        }

        const questionRef = doc(db, 'questions', classSubject.questionSetId);
        const questionSnap = await getDoc(questionRef);
        if (!questionSnap.exists()) {
          setError("Question set not found");
          setLoading(false);
          return;
        }

        const subjectDoc = doc(db, 'subjects', subjectId);
        const subjectSnap = await getDoc(subjectDoc);
        const subjectName = subjectSnap.exists() ? subjectSnap.data().name : 'Unknown';

        setSubjectDetails({
          name: subjectName,
          questionSetId: classSubject.questionSetId,
          duration: classSubject.duration || 0,
          activationDate: classSubject.activationDate,
          deactivationDate: classSubject.deactivationDate,
          questionCount: classSubject.questionCount || 0,
          marksPerQuestion: classSubject.marksPerQuestion || 0
        });

        const allQuestions = questionSnap.data().questions || [];
        setQuestions(allQuestions);
        const shuffled = shuffleArray([...allQuestions]).slice(0, classSubject.questionCount || allQuestions.length);

        const questionsWithShuffledOptions = shuffled.map(q => {
          const options = [...q.options];
          const shuffledIndices = Array.from({ length: options.length }, (_, i) => i);
          shuffleArray(shuffledIndices);
          const shuffledOptions = shuffledIndices.map(i => options[i]);
          const newCorrectIndex = shuffledIndices.indexOf(q.correctIndex);
          return {
            ...q,
            options: shuffledOptions,
            correctIndex: newCorrectIndex
          };
        });
        setShuffledQuestions(questionsWithShuffledOptions);

      } catch (error) {
        setError("Failed to load exam");
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [student, subjectId, currentSessionId, currentTermId, currentClassId]);
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || isIntroModalOpen) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isIntroModalOpen]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleAnswerChange = (questionIndex, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: option
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const endTime = new Date();
      const timeUsedSeconds = Math.floor((endTime - startTime) / 1000);
      setTimeUsed(timeUsedSeconds);

      const calculatedScore = calculateScore();

      const studentRef = doc(db, 'students', student.id);
      const studentSnap = await getDoc(studentRef);

      if (!studentSnap.exists()) {
        setError("Failed to save score: Student not found");
        setIsSubmitting(false);
        return;
      }

      const studentData = studentSnap.data();

      // Use the passed currentClassId (most reliable)
      const targetClassId = currentClassId;

      if (!targetClassId) {
        setError("Class information missing. Please restart the exam from dashboard.");
        setIsSubmitting(false);
        return;
      }

      const updatedEnrollments = studentData.enrollments.map(enroll => {
        if (enroll.classId === targetClassId) {
          const updatedSubjects = enroll.subjects.map(sub => {
            if (sub.subjectId === subjectId) {
              return {
                ...sub,
                scores: {
                  ...(sub.scores || {}),
                  exam: calculatedScore,
                  examTakenAt: new Date().toISOString(),
                  examSessionId: currentSessionId,
                  examTermId: currentTermId
                }
              };
            }
            return sub;
          });
          return { ...enroll, subjects: updatedSubjects };
        }
        return enroll;
      });

      await updateDoc(studentRef, { enrollments: updatedEnrollments });

      setScore(calculatedScore);
      setIsScoreModalOpen(true);
    } catch (error) {
      setError("Failed to save exam score. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };






  const calculateScore = () => {
    let score = 0;
    shuffledQuestions.forEach((question, index) => {
      if (answers[index] && answers[index] === question.options[question.correctIndex]) {
        score += subjectDetails.marksPerQuestion || 0;
      }
    });
    return score;
  };

  const startExam = () => {
    setIsIntroModalOpen(false);
    setTimeLeft(subjectDetails.duration * 60);
    setStartTime(new Date());
  };

  const handleBack = () => {
    setIsBackConfirmOpen(true);
  };

  const confirmBack = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await handleSubmit();
      setIsBackConfirmOpen(false);
      navigate('/student-dashboard', { state: { student } });
    } catch (error) {
      setError("Failed to save score before returning to dashboard");
      setIsSubmitting(false);
    }
  };

  const handleConfirmSubmit = () => {
    setIsSubmitConfirmOpen(true);
  };

  const confirmSubmit = () => {
    setIsSubmitConfirmOpen(false);
    handleSubmit();
  };

  const closeScoreModal = () => {
    setIsScoreModalOpen(false);
    navigate('/student-dashboard', { state: { student } });
  };

  const handlePageChange = (direction) => {
    setTransition(direction);
    setTimeout(() => {
      setCurrentPage(prev => direction === 'left' ? Math.max(prev - 1, 0) : Math.min(prev + 1, totalPages - 1));
      setTransition(null);
    }, 300); // Match CSS transition duration
  };

  const currentQuestions = shuffledQuestions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const totalPages = Math.ceil(shuffledQuestions.length / questionsPerPage);

  const getPerformanceImage = () => {
    if (score >= 50) {
      return <i className="fa-regular fa-circle-check font-green modal-icon"></i>;
    } else if (score >= 30) {
      return <i className="fa-solid fa-circle-exclamation font-orange modal-icon"></i>;
    } else {
      return <i className="fa-regular fa-circle-xmark font-red modal-icon"></i>;
    }
  };

  if (!student) {
    return (
      <div className="column align-center justify-center" style={{margin: '50px 20px'}}>
        <p className="error-message size3 align-center justify-center">No student data available</p>
        <Link to="/student-login" className="back-link">
          <button className="login-button">Back to Login</button>
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="column align-center justify-center" style={{margin: '50px 20px'}}>
        <p className="loading-text">Loading exam...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="column align-center justify-center" style={{margin: '50px 20px'}}>
        <p className="error-message align-center justify-center">{error}</p>
        <button
          className="login-button"
          onClick={() => navigate('/student-dashboard', { state: { student } })}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="student-dashboard-container column align-center">
        <div className="student-name">
          <span className="bold size5">{student.firstName} {student.middleName} {student.lastName}</span><br />
          <span className="size5">{subjectDetails?.name} CBT</span>
        </div>

        <p className="male-female-stat">
          Duration: {subjectDetails?.duration ? `${subjectDetails.duration} minutes` : 'Not set'} | Time Left: {formatTime(timeLeft)}
        </p>

        <div className="scroll-fixed scroll-fixed-questions">
          <div
            className="class-list class-list-d scrollable"
            style={{
              transform: transition === 'left' ? 'translateX(100%)' : transition === 'right' ? 'translateX(-100%)' : 'translateX(0)',
              opacity: transition ? 0 : 1,
              transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
              width: '100%',
            }}
          >
            {currentQuestions.map((question, pageIndex) => {
              const globalIndex = currentPage * questionsPerPage + pageIndex;
              return (
                <div key={globalIndex} className="class-item class-item-questions column">
                  <p>
                    <span className="size5 bold">{globalIndex + 1}. </span>
                    {question.questionText}
                  </p>
                  <div className="column gap-10">
                    {question.options.map((option, optIndex) => (
                      <label key={optIndex} className="male-female-stat">
                        <input
                          type="radio"
                          name={`question-${globalIndex}`}
                          value={option}
                          checked={answers[globalIndex] === option}
                          onChange={() => handleAnswerChange(globalIndex, option)}
                          className="input-checkbox gold-tick"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="row align-center justify-center gap-15 exam-nav-buttons" style={{ marginTop: '0px' }}>
          <button
            className="list-btn"
            onClick={() => handlePageChange('left')}
            disabled={currentPage === 0}
          >
            <i className="fas fa-arrow-left"></i>
          </button>
          <p className="male-female-stat">Page {currentPage + 1} of {totalPages}</p>
          <button
            className="list-btn"
            onClick={() => handlePageChange('right')}
            disabled={currentPage === totalPages - 1}
          >
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>

        <div className="row align-center gap-15 exam-buttons">
          <button
            className="login-button"
            onClick={handleBack}
            disabled={isSubmitting}
          >
            Dashboard
          </button>
          <button
            className="login-button"
            onClick={handleConfirmSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>

      {/* Intro Modal */}
      <Modal
        isOpen={isIntroModalOpen}
        onRequestClose={() => {}}
        contentLabel="Exam Instructions"
        className="custom-modal custom-modal1 modal-scroll"
        overlayClassName="custom-modal-overlay custom-modal-overlay1"
      >
        <p className="bold size5 caps exam-instructions">Exam Instructions</p>
        <div className="column exam-instructions">
          <p><strong>Subject:</strong> {subjectDetails?.name}</p>
          <p><strong>Number of Questions:</strong> {subjectDetails?.questionCount}</p>
          <p><strong>Duration:</strong> {subjectDetails?.duration} minutes</p>
          <p><strong>Total Marks:</strong> 60</p>
          <p><strong>Instructions:</strong></p>
          <ul className="exam-instructions">
            <li>Answer all questions.</li>
            <li>Each question carries {subjectDetails?.marksPerQuestion} marks.</li>
            <li>The exam will auto-submit when time expires.</li>
            <li>Do not refresh the page.</li>
            <li>Do not exit the page or your progress will be saved and scored.</li>
            <li>Good luck!</li>
          </ul>
          <div className="row align-center justify-center gap-1">
            <button 
              className="login-button" 
              type="button" 
              onClick={startExam}
              style={{maxWidth: '600px'}}
            >
              OK
            </button>
          </div>
        </div>
      </Modal>

      {/* Back Confirmation Modal */}
      <Modal
        isOpen={isBackConfirmOpen}
        onRequestClose={() => setIsBackConfirmOpen(false)}
        contentLabel="Confirm Back"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <p className="bold size5 caps">Confirm</p>
        <p>Are you sure you want to go back to dashboard? Your progress will be saved and scored.</p>
        <div className="row align-center justify-center gap-1">
          <button 
            className="login-button" 
            type="button" 
            onClick={() => setIsBackConfirmOpen(false)}
          >
            Cancel
          </button>
          <button 
            className="login-button" 
            type="button" 
            onClick={confirmBack}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Yes'}
          </button>
        </div>
      </Modal>

      {/* Submit Confirmation Modal */}
      <Modal
        isOpen={isSubmitConfirmOpen}
        onRequestClose={() => setIsSubmitConfirmOpen(false)}
        contentLabel="Confirm Submit"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <p className="bold size5 caps">Confirm</p>
        <p>Are you sure you want to submit the exam? Your score will be recorded once you submit your answers.</p>
        <div className="row align-center justify-center gap-1">
          <button 
            className="login-button" 
            type="button" 
            onClick={() => setIsSubmitConfirmOpen(false)}
          >
            Cancel
          </button>
          <button 
            className="login-button" 
            type="button" 
            onClick={confirmSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Yes'}
          </button>
        </div>
      </Modal>

      {/* Score Modal */}
      <Modal
        isOpen={isScoreModalOpen}
        onRequestClose={closeScoreModal}
        contentLabel="Exam Score"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <p className="bold size5 caps exam-score">Your Score</p>
        <div className="align-center justify-center">{getPerformanceImage()}</div>
        <p className="exam-score">You scored {score} out of 60.</p>
        <p className="exam-score">Time Used: {timeUsed ? formatTime(timeUsed) : 'N/A'} / {subjectDetails?.duration ? formatTime(subjectDetails.duration * 60) : 'N/A'}</p>
        <div className="row align-center justify-center gap-1">
          <button 
            className="login-button" 
            type="button" 
            onClick={closeScoreModal}
            style={{maxWidth: '600px'}}
          >
            OK
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Exam;